import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useScreen } from '@modules/tourdanji/src/react/hooks/useScreen';

import { TourDanjiModule } from '../TourDanjiModule';

import { GUIDE_3D_TOUR_LINES, GUIDE_3D_TOUR_TITLE } from './GuideContents';
import styles from './styles/Loading.module.css';

export type LoadingPanelProps = {
  messages?: string[];
};

export const LoadingPanel = ({ messages }: LoadingPanelProps) => {
  const screenInfo = useScreen();
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();

  const loadingTextList = useMemo(() => {
    return messages
      ? messages
      : [
          '단지 정보 확인을 시작합니다.',
          '위성사진을 분석하는 중입니다.',
          '건물 설계도를 분석하는 중입니다.',
          '지형을 올리는 중입니다.',
        ];
  }, [messages]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const [isStop, setStop] = useState(false);
  const intervalId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    intervalId.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % loadingTextList.length);
    }, 2000);

    return () => clearInterval(intervalId.current);
  }, [loadingTextList.length, intervalId]);

  useEffect(() => {
    if (!zed) return;
    zed.api.observer.onLoadCompleted.addOnce(() => {
      setShouldFadeOut(true);
      clearInterval(intervalId.current);
      setTimeout(() => {
        setStop(true);
      }, 1000);
    });
  }, [zed]);

  if (isStop) {
    return null;
  }

  return (
    <div style={{ zIndex: Layers.UI_MODAL }} className={`${styles.container} ${shouldFadeOut ? styles.fadeOut : ''}`}>
      <span className={`${styles.loadingText} ${styles.textAnimation}`} style={{ 
          whiteSpace: 'pre-line',
          fontSize: 'clamp(16px, 4vw, 24px)',
          wordBreak: 'keep-all',
          wordWrap: 'break-word',
          maxWidth: '100vw',
          textAlign: 'center',
          fontFamily: 'Pretendard, Spoqa Han Sans Neo, sans-serif',
        }}>
        {loadingTextList[currentIndex]}
      </span>
      {screenInfo.screenType && (
        <div className={screenInfo.isInComponent ? styles.guideContainerSmall : styles.guideContainer}>
          <div className={styles.guideBackground}>
            <strong className={screenInfo.isInComponent ? styles.guideTitleSmall : styles.guideTitle}>
              {GUIDE_3D_TOUR_TITLE}
            </strong>
            <div className={screenInfo.isInComponent ? styles.guideTextSmall : styles.guideText}>
              {GUIDE_3D_TOUR_LINES.map((content, index) => (
                <span key={index}>{content}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
